import React, { useEffect, useState } from 'react'; 
import KPIHeader from '../../Header/KPIHeader';
import KPISubHeader from '../../Header/SubHeader/KPISubHeader';
import { useSelector } from "react-redux";
import ComparsionDefinitions from '../../Defintions/ComparsionDefinitions';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid
} from "recharts";
import HaqdarshakLeaderboard from '../../KPI/Comparison/HDLeaderboard';
import HaqdarshakLeaderboardDS from '../../KPI/Comparison/HDLeaderboardDS';
import HDPerformanceDashboard from '../../KPI/Comparison/HDPerformance';

const Comparison = () => {
  const email = useSelector((state) => state.user.email);
  const projectName = useSelector((state) => state.user.projectName);
  const pid = useSelector((state) => state.user.projectId);
  const [activeTab, setActiveTab] = useState("BR"); 

  const criteriaOptions = [
    { value: 'CitizenDistrict', label: 'District' },
    { value: 'State', label: 'State' },
    { value: 'target_group', label: 'Target Group' },
    { value: 'SchemeName', label: 'Scheme Name' }
  ];

  const [selectedCriteria, setSelectedCriteria] = useState("CitizenDistrict");
  const [chartData, setChartData] = useState([]);
  const [topPerforming, setTopPerforming] = useState([]);
  const [bottomPerforming, setBottomPerforming] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [weeksPerPage, setWeeksPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [loading,setLoading]=useState(false)

  const [stateOptions, setStateOptions] = useState([]);
  const [selectedState, setSelectedState] = useState('');

  const colors = [
    "#e91e63", "#9c27b0", "#2196f3", "#4caf50", "#ff9800",
    "#795548", "#607d8b", "#f44336", "#3f51b5", "#00bcd4"
  ];

  // Get topItems for chart dynamically
  const topItems = chartData.length > 0 
    ? Object.keys(chartData[0]).filter(key => key !== "week_number") 
    : [];

  // Fetch list of states for dropdown
  useEffect(() => {
    if (!pid) return;
    const fetchStates = async () => {
      try {
        const res = await fetch(`https://api-project360-insights.haqdarshak.com/kpi/scheme-diversity/unique-state/?pid=${pid}`);
        const json = await res.json();
        setStateOptions(json.states || []);
      } catch (err) {
        console.error("Error fetching states:", err);
        setStateOptions([]);
      }
    };
    fetchStates();
  }, [pid]);

  // Fetch week-wise chart data
  useEffect(() => {
    if (!pid) return;

    const fetchWeekWiseData = async () => {
      setLoading(true);
      try {
        let url = `https://api-project360-insights.haqdarshak.com/kpi/comparison/week-wise/?pid=${encodeURIComponent(pid)}&criteria=${encodeURIComponent(selectedCriteria)}`;
        if (selectedCriteria === 'CitizenDistrict' && selectedState) {
          url += `&state=${encodeURIComponent(selectedState)}`;
        }

        const res = await fetch(url);
        const data = await res.json();
        setChartData(data.week_wise_data || []);
        setCurrentPage(0);
      } catch (err) {
        console.error("Error fetching week-wise data:", err);
        setChartData([]);
      }
      finally{
        setLoading(false)
      }
    };

    fetchWeekWiseData();
  }, [pid, selectedCriteria, selectedState]);

  // Fetch performance data for tables
  useEffect(() => {
    if (!pid) return;

    const fetchPerformanceData = async () => {
      setIsLoading(true);
      try {
        let url = `https://api-project360-insights.haqdarshak.com/kpi/comparison/performance/?pid=${encodeURIComponent(pid)}&criteria=${encodeURIComponent(selectedCriteria)}`;
        if (selectedCriteria === 'CitizenDistrict' && selectedState) {
          url += `&state=${encodeURIComponent(selectedState)}`;
        }

        const res = await fetch(url);
        const data = await res.json();
        setTopPerforming(data.leaderboard?.top_5 || []);
        setBottomPerforming(data.leaderboard?.bottom_5 || []);
      } catch (err) {
        console.error("Error fetching performance data:", err);
        setTopPerforming([]);
        setBottomPerforming([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPerformanceData();
  }, [pid, selectedCriteria, selectedState]);

  useEffect(() => {
    if (selectedCriteria !== 'CitizenDistrict') {
      setSelectedState('');
    }
  }, [selectedCriteria]);

  const paginatedData = chartData.slice(
    currentPage * weeksPerPage,
    (currentPage + 1) * weeksPerPage
  );

  const CustomLegend = ({ payload }) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', fontSize: '1.2rem', marginBottom: '3vh', marginLeft: '5vw' }}>
      {(payload || []).map((entry, index) => (
        <div key={`legend-${index}`} style={{ marginRight: 16, display: 'flex', alignItems: 'center' }}>
          <div style={{ width: 12, height: 12, backgroundColor: entry.color, marginRight: 6 }} />
          {entry.value}
        </div>
      ))}
    </div>
  );

  const handleRedirect = () => {
    window.open('https://usersreport-insights.haqdarshak.com/','_blank');
  };

  return (
    <div>
      <div className='z-50'><KPIHeader /><KPISubHeader /></div>
      <div className="mt-24">
        {/* Project Title Section */}
        <div className="px-6 py-2 flex items-center justify-between border-b border-gray-200 mb-3 bg-white rounded-lg shadow-sm">
          <div className="flex items-center space-x-4">
            <div className="bg-blue-100 p-2 rounded-lg">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-800 truncate max-w-xs md:max-w-md">{projectName}</h1>
          </div>
          <span><ComparsionDefinitions/></span>
          <span className="bg-gradient-to-r from-purple-100 to-indigo-100 text-purple-800 text-sm font-semibold px-4 py-2 rounded-full shadow-sm flex items-center">
            PID: {pid}
          </span>
        </div>

        <div className='ml-[3vw] flex items-center gap-4 mb-4'>
          <div>
            <label className='text-xl font-bold mr-2'>Select Criteria :</label>
            <select
              value={selectedCriteria}
              onChange={(e) => setSelectedCriteria(e.target.value)}
              className="border border-gray-300 p-2 rounded ml-2"
            >
              {criteriaOptions.map((item, idx) => <option key={idx} value={item.value}>{item.label}</option>)}
            </select>
          </div>

          {selectedCriteria === 'CitizenDistrict' && (
            <div>
              <label className='text-xl font-bold mr-2'>Filter by State (optional) :</label>
              <select
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                className="border border-gray-300 p-2 rounded ml-2"
              >
                <option value=''>-- All States --</option>
                {stateOptions.map((st, i) => <option key={i} value={st}>{st}</option>)}
              </select>
            </div>
          )}
        </div>

        {/* Chart Section */}
        {/* <div className="mt-6 border-gray-600 shadow-md shadow-black rounded p-2 mr-[3vw] ml-[3vw]"> */}
          {/* Controls Section */}
          {/* <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-4">
            <div className="flex flex-row w-full md:w-1/3">
              <div>
                <label htmlFor="weeks-per-page" className="block text-lg font-medium text-gray-500 mb-1">
                  Weeks per page:
                </label>
              </div>
              <div className='ml-4'>
                <select
                  id="weeks-per-page"
                  value={weeksPerPage}
                  onChange={(e) => setWeeksPerPage(Number(e.target.value))}
                  className="w-full px-12 py-1 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={30}>30</option>
                  <option value={40}>40</option>
                  <option value={80}>80</option>
                  <option value={150}>150</option>
                  <option value={300}>300</option>
                  <option value={500}>500</option>
                </select>
              </div>
            </div>

            <div className="text-lg text-gray-600">
              Showing weeks {currentPage * weeksPerPage + 1} to {' '}
              {Math.min((currentPage + 1) * weeksPerPage, chartData.length)} of {' '}
              {chartData.length}
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 0))}
                disabled={currentPage === 0 || isLoading}
                className={`px-4 py-2 rounded-md ${
                  currentPage === 0
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : 'bg-purple-600 text-white hover:bg-purple-700'
                }`}
              >
                Previous
              </button>

              <span className="px-4 py-2 bg-gray-100 rounded-md">
                Page {currentPage + 1} of {Math.ceil(chartData.length / weeksPerPage) || 1}
              </span>

              <button
                onClick={() => setCurrentPage(prev =>
                  (prev + 1) * weeksPerPage < chartData.length ? prev + 1 : prev
                )}
                disabled={(currentPage + 1) * weeksPerPage >= chartData.length || isLoading}
                className={`px-4 py-2 rounded-md ${
                  (currentPage + 1) * weeksPerPage >= chartData.length
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : 'bg-purple-600 text-white hover:bg-purple-700'
                }`}
              >
                Next
              </button>
            </div>
          </div>

          {/* Chart Section */}
          {/* <div style={{ height: "400px" }}>
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
              </div>
            ) : (
              <LineChart
                width={Math.min(1200, window.innerWidth - 40)}
                height={400}
                data={paginatedData}
                margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="week_number"
                  tickFormatter={(value) => `Week ${value}`}
                  label={{
                    value: "Weeks",
                    position: "insideBottom",
                    offset: -10,
                    style: { textAnchor: "middle", fontSize: 14, fontWeight: 'bold' }
                  }}
                />
                <YAxis
                  label={{
                    value: "BR Count",
                    angle: -90,
                    position: "insideLeft",
                    style: { textAnchor: "middle", fontSize: 14, fontWeight: 'bold' }
                  }}
                />
                <Tooltip
                  formatter={(value, name) => [`${value}`, name]}
                  labelFormatter={(label) => `Week ${label}`}
                />
                <Legend content={<CustomLegend />} />
                {topItems.map((item, idx) => (
                  <Line
                    key={item}
                    type="monotone"
                    dataKey={item}
                    name={item}
                    stroke={colors[idx % colors.length]}
                    strokeWidth={3}
                    dot={{ r: 5, strokeWidth: 2 }}
                    activeDot={{ r: 7 }}
                  />
                ))}
              </LineChart>
            )}
          </div>
        </div> */} 

        {/* Tables Section */}
        <div className='grid grid-cols-2 mr-[3vw] ml-[3vw] mt-6 gap-6'>
          {/* Top Performing */}
          <div className="w-full p-4 bg-white rounded shadow">
            <h2 className="text-xl font-bold mb-4 text-gray-800">Top Performing</h2>
            {isLoading ? (
              <div className="flex items-center justify-center h-40">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-200 bg-white shadow rounded-lg">
                  <thead>
                    <tr className="bg-gray-100 text-gray-700 font-semibold text-left">
                      <th className="px-4 py-2 border">Rank</th>
                      <th className="px-4 py-2 border">Name</th>
                      <th className="px-4 py-2 border">BR Cases</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topPerforming?.length > 0 ? topPerforming.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-2 border font-medium">{item.rank}</td>
                        <td className="px-4 py-2 border">{item.name}</td>
                        <td className="px-4 py-2 border">{item.br_value}</td>
                      </tr>
                    )) : <tr><td colSpan={3} className="text-center px-4 py-2 border">No data available</td></tr>}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Bottom Performing */}
          <div className="w-full p-4 bg-white rounded shadow">
            <h2 className="text-xl font-bold mb-4 text-gray-800">Bottom Performing</h2>
            {isLoading ? (
              <div className="flex items-center justify-center h-40">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-200 bg-white shadow rounded-lg">
                  <thead>
                    <tr className="bg-gray-100 text-gray-700 font-semibold text-left">
                      <th className="px-4 py-2 border">Rank</th>
                      <th className="px-4 py-2 border">Name</th>
                      <th className="px-4 py-2 border">BR Cases</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bottomPerforming?.length > 0 ? bottomPerforming.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-2 border font-medium">{item.rank}</td>
                        <td className="px-4 py-2 border">{item.name}</td>
                        <td className="px-4 py-2 border">{item.br_value}</td>
                      </tr>
                    )) : <tr><td colSpan={3} className="text-center px-4 py-2 border">No data available</td></tr>}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Redirect Button */}
        <button onClick={handleRedirect} className="group relative ml-[30vw] mb-2 mt-4 inline-flex items-center justify-center px-6 py-3 font-medium text-white rounded-full shadow-lg bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700">
          Click here to see Achievement Dashboard
        </button>

        {/* Toggle Leaderboard */}
        <div className="mt-8">
          <div className="flex space-x-2 mb-4 ml-[40vw]">
            <button onClick={() => setActiveTab("BR")} className={`px-4 py-2 rounded-lg font-medium shadow-md transition ${activeTab === "BR" ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"}`}>BR</button>
            <button onClick={() => setActiveTab("DS")} className={`px-4 py-2 rounded-lg font-medium shadow-md transition ${activeTab === "DS" ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"}`}>DS</button>
          </div>
          {activeTab === "BR" ? <HaqdarshakLeaderboard /> : <HaqdarshakLeaderboardDS />}
        </div>
        <div>
          <HDPerformanceDashboard/>
        </div>
      </div>
    </div>
  );
};

export default Comparison;
